import {useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {fetchEventSource} from '@microsoft/fetch-event-source';

// fetchEventSource retries unless onerror re-throws. Use a named class so onerror
// can distinguish non-retryable failures (401, 404) from transient network errors.
class NonRetryableError extends Error {}
import axios from 'axios';

import {IPatientAssignment, InterventionStatusCode} from '../../redux/intervention/interfaces';
import {
  getAssistantSession,
  getNotificationCount,
  getNotifications,
  getPatientAssignments,
  getPatientCareTeam,
  getPatientLibrary,
  getPatientMedications,
  getPatientRun,
  interventionAsyncActions,
  notificationAsyncActions
} from '../../redux/pageAsyncActions';

// Statuses that warrant an open SSE connection.
const ACTIVE_STATUSES = new Set<InterventionStatusCode>(['enrolled', 'in_progress', 'screening', 'paused', 'requested']);

// Event code → which queries to reload. Derived from the home-event-stream invalidation map.
const ASSIGNMENT_EVENTS = new Set([
  'participant_enrolled', 'participant_paused', 'participant_resumed',
  'participant_completed', 'participant_withdrawn', 'participant_screen_failed'
]);

const RUN_EVENTS = new Set([
  'activity_assigned', 'module_occurrence_created', 'module_unlocked', 'next_module_started',
  'step_started', 'step_completed', 'step_reviewed', 'step_skipped',
  'medication_dose_due', 'medication_dose_taken', 'medication_dose_skipped', 'medication_dose_missed'
]);

const ASSISTANT_EVENTS = new Set([
  'ai_conversation_started', 'ai_conversation_completed', 'ai_conversation_abandoned'
]);

function buildHeaders(): Record<string, string> {
  const common = axios.defaults.headers.common as Record<string, string>;
  const headers: Record<string, string> = {
    Authorization: common.Authorization ?? '',
    API_KEY: common.API_KEY ?? (process.env.REACT_APP_API_KEY ?? '')
  };

  if (common.CLEVER_TOKEN) {
    headers.CLEVER_TOKEN = common.CLEVER_TOKEN;
  }

  return headers;
}

// Opens one SSE stream per active ActivityParticipant. Tears down all streams on unmount
// or when the set of active activityIds changes (re-subscribes with fresh controllers).
export const useInterventionSse = (assignments: IPatientAssignment[]): void => {
  const dispatch = useDispatch();

  // Stable string key that changes only when the active assignment set changes.
  const activeKey = assignments
    .filter((a) => ACTIVE_STATUSES.has(a.statusCode))
    .map((a) => `${a.activityId}:${a.activityParticipantId}`)
    .sort()
    .join(',');

  useEffect(() => {
    if (!activeKey) {
      return () => {};
    }

    const activeAssignments = assignments.filter((a) => ACTIVE_STATUSES.has(a.statusCode));
    const base = process.env.REACT_APP_API_URL ?? '';

    const controllers: AbortController[] = activeAssignments.map((a) => {
      const ctrl = new AbortController();
      const url = `${base}/inspector/activity/${a.activityId}/stream`;

      fetchEventSource(url, {
        headers: buildHeaders(),
        signal: ctrl.signal,

        onopen: async (response) => {
          // 401 and 404 are non-retryable: no eligible AP or session expired.
          if (response.status === 401 || response.status === 404) {
            throw new NonRetryableError(`SSE ${response.status} on activity ${a.activityId}`);
          }
        },

        onmessage: (msg) => {
          const code = msg.event;

          if (!code) {
            return;
          }

          if (code === 'crisis_detected') {
            // Reload everything on crisis.
            dispatch(interventionAsyncActions[getPatientAssignments]());
            dispatch(interventionAsyncActions[getPatientRun](a.activityParticipantId));
            dispatch(interventionAsyncActions[getPatientLibrary](a.activityParticipantId));
            dispatch(interventionAsyncActions[getPatientMedications]());
            dispatch(interventionAsyncActions[getPatientCareTeam]());
            dispatch(interventionAsyncActions[getAssistantSession]());
            dispatch(notificationAsyncActions[getNotifications]({indRead: false, offset: 0, append: false}));
            dispatch(notificationAsyncActions[getNotificationCount]());

            return;
          }

          if (ASSIGNMENT_EVENTS.has(code)) {
            dispatch(interventionAsyncActions[getPatientAssignments]());
          }
          if (RUN_EVENTS.has(code)) {
            dispatch(interventionAsyncActions[getPatientRun](a.activityParticipantId));
          }
          if (ASSISTANT_EVENTS.has(code)) {
            dispatch(interventionAsyncActions[getAssistantSession]());
          }
          if (code === 'notification_dispatched') {
            dispatch(notificationAsyncActions[getNotifications]({indRead: false, offset: 0, append: false}));
            dispatch(notificationAsyncActions[getNotificationCount]());
          }
        },

        onerror: (err) => {
          if (err instanceof NonRetryableError) {
            throw err;
          }
          console.error(`SSE error on activity ${a.activityId}:`, err);
        }
      }).catch((err) => {
        // NonRetryableErrors thrown from onopen/onerror surface here; log and swallow.
        if (!(err instanceof NonRetryableError)) {
          console.error(`SSE stream failed for activity ${a.activityId}:`, err);
        }
      });

      return ctrl;
    });

    return () => {
      controllers.forEach((c) => c.abort());
    };
  }, [activeKey, dispatch]);
};
